import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CirclePlus, SquarePen, Trash2, ShieldBan, Search, X, CheckSquare, Loader2 } from "lucide-react";

import Table from "../../../components/common/Table";
import Pagination from "../../../components/common/Pagination";
import Modal from "../../../components/common/Modal";
import useAuthStore from "../../../store/auth.store";

export default function ListTemplate({
  title,
  columns,
  fetchFn,
  deleteFn,
  getEditPath,
  getAddPath,
  extraRowActions,
  searchPlaceholder,
  // Optional: enable bulk selection + render a bulk-action toolbar when ≥1
  // row is selected. `bulkActions` receives the selected IDs and a reset()
  // helper so it can clear selection after an action completes.
  bulkActions, // (selectedIds, reset, reloadList) => ReactNode | null
  // Optional slot rendered between the title block and the Add button —
  // used by e.g. the Tournaments page for a "+ New Folder" button.
  extraHeaderActions,
  // Optional slot rendered between the search bar and the table — used
  // for filter chips (e.g. per-folder filter on the Tournaments page).
  // Receives a `reload` callback so the chip strip can force a refetch
  // after a filter change without re-mounting the whole page.
  filterStrip, // ReactNode | ({ reload }) => ReactNode
  // Optional signal to force the fetch to re-run (bump a counter when
  // an out-of-band mutation happens, e.g. after moving rows to a folder).
  reloadSignal,
}) {
  const isSuperuser = useAuthStore((s) => !!s.user?.is_superuser);

  // 2026-06-07 — search bar optimisations:
  //   - URL sync via ?q= + ?page= so reload / share preserves the
  //     query, and the back/forward buttons step through searches.
  //   - Debounce 300 → 200ms (snappier without storming the API).
  //   - Enter commits instantly; Esc clears when input has focus.
  //   - Inline spinner inside the input while a fetch is in flight.
  //   - "/" focuses the bar from anywhere on the page (skipped when
  //     the user is already typing in another input).
  //   - Stale-response guard via a fetch-sequence ref so a slow
  //     response from a prior search can't overwrite a newer one's
  //     results.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const initialPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const [data, setData] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [search, setSearch] = useState(initialSearch);        // committed value used by the request
  const [searchInput, setSearchInput] = useState(initialSearch); // what's in the input box
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  // Sort state lives here so the Table component can stay presentational.
  // null = server order; { key, dir } when the user has chosen one.
  const [sortState, setSortState] = useState(null);
  // Bulk-selection — Set of row IDs; outlives page changes and sort changes
  // so the operator can select on page 1, paginate, and the count still
  // shows the right total.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Track if the user has interacted with the search box so the empty-state
  // card doesn't flash for filtered-to-zero searches.
  const searchUsedRef = useRef(initialSearch.length > 0);
  // Monotonic fetch counter — a stale slow response can't clobber a
  // newer one's result if its sequence number is lower than the last
  // started request.
  const fetchSeqRef = useRef(0);
  const searchInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const mySeq = ++fetchSeqRef.current;
    return fetchFn(page, { search: search || undefined })
      .then((res) => {
        if (mySeq === fetchSeqRef.current) setData(res.data);
      })
      .finally(() => {
        if (mySeq === fetchSeqRef.current) setLoading(false);
      });
  }, [page, search, fetchFn]);

  useEffect(() => { load(); }, [load, reloadSignal]);

  // Mirror committed search + page into the URL so reload / share /
  // browser back preserves the query. Use replace mode for debounced
  // search updates so we don't pollute history with intermediate
  // keystrokes; pagination changes go through push (separate
  // user-driven action).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (search) next.set("q", search); else next.delete("q");
    if (page > 1) next.set("page", String(page)); else next.delete("page");
    // Compare against current to avoid an extra navigation when nothing
    // actually changed (re-render storm during typing).
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  // Debounce input → committed search; reset to page 1 when search
  // changes. 200ms is the sweet spot — slower than the typing tempo of
  // the average operator but well below the noticeable-lag threshold.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput.trim());
        setPage(1);
        searchUsedRef.current = true;
      }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // "/" keyboard shortcut — focuses the search bar from anywhere on
  // the page. Skipped when the user is already typing in an input /
  // textarea / contenteditable, so it doesn't hijack legitimate "/"
  // keystrokes (file paths, comments, etc).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select"
          || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Enter inside the search box commits immediately (skip the debounce
  // wait); Esc clears the box.
  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = searchInput.trim();
      if (trimmed !== search) {
        setSearch(trimmed);
        setPage(1);
        searchUsedRef.current = true;
      }
    } else if (e.key === "Escape" && searchInput) {
      e.preventDefault();
      setSearchInput("");
    }
  };

  const total = data?.count ?? 0;
  const shown = data?.results?.length ?? 0;
  const filtered = !!search;

  const toggleId = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (ids, select) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((i) => next.add(i));
      else ids.forEach((i) => next.delete(i));
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());

  const selectable = !!bulkActions;
  const bulkBar = useMemo(() => {
    if (!selectable || selectedIds.size === 0) return null;
    return bulkActions(Array.from(selectedIds), clearSelection, load);
  }, [selectable, selectedIds, bulkActions, load]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold">{title}</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {loading
              ? "Loading…"
              : <>Showing <span className="text-text-primary font-medium">{shown}</span> of <span className="text-text-primary font-medium">{total.toLocaleString()}</span> {title.toLowerCase()}{filtered ? " (filtered)" : ""}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {extraHeaderActions}
          <a
            href={getAddPath()}
            className="bg-button-primary hover:brightness-110 text-white px-4 py-2 rounded-md flex gap-2 items-center w-fit text-sm font-semibold transition-all"
          >
            <CirclePlus className="w-4" />
            Add {title.replace(/s$/, "")}
          </a>
        </div>
      </div>

      {/* Search bar — sticky-top so it stays visible on long lists.
          Right-side cluster shows a spinner while a fetch is in flight,
          then a Clear (✕) once the box has content. The "/" hint sits
          on the far right at md+ widths so power users learn it
          without it taking space on phones. */}
      <div className="relative mb-4 max-w-xl sticky top-0 z-10 bg-bg-main py-1">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={searchPlaceholder || `Search ${title.toLowerCase()}… (press / to focus)`}
          aria-label={`Search ${title.toLowerCase()}`}
          className="w-full bg-bg-primary border border-border-default rounded-md py-2 pl-9 pr-20 text-sm text-text-primary placeholder-text-secondary/60 focus:outline-none focus:border-button-primary transition-colors"
        />
        {/* Right-side cluster: spinner (while loading) + clear (✕) +
            "/" hint. Absolute-positioned so the input field doesn't
            re-flow when any of them appears / disappears. */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && (
            <Loader2
              className="w-3.5 h-3.5 text-text-secondary animate-spin"
              aria-hidden="true"
            />
          )}
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="text-text-secondary hover:text-text-primary transition-colors p-1"
              title="Clear search (Esc)"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {!searchInput && !loading && (
            <kbd className="hidden md:inline-block px-1.5 py-0.5 rounded border border-border-default bg-bg-secondary text-[10px] font-mono text-text-secondary">
              /
            </kbd>
          )}
        </div>
      </div>

      {/* Optional filter chip strip. Rendered between the search bar
          and the bulk toolbar so it stays visible while scrolling
          through selection actions. Function form receives `reload`
          so the chip strip can trigger a refetch after a filter
          change (also handled by reloadSignal but useful for
          immediate feedback). */}
      {filterStrip && (
        <div className="mb-3">
          {typeof filterStrip === "function" ? filterStrip({ reload: load }) : filterStrip}
        </div>
      )}

      {/* Bulk action toolbar — appears only when bulkActions prop is set
          AND ≥1 row is selected. Sticky-feel via amber border so the
          operator never loses track of their selection. */}
      {selectable && selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          <CheckSquare className="w-4 h-4 text-amber-300" />
          <span className="text-amber-200">
            {selectedIds.size} {title.toLowerCase().replace(/s$/, "")}{selectedIds.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex-1" />
          {bulkBar}
          <button
            onClick={clearSelection}
            className="ml-1 px-3 py-1 rounded bg-bg-secondary text-text-secondary border border-border-default text-xs uppercase tracking-wider hover:bg-bg-main"
          >
            Clear
          </button>
        </div>
      )}

      {loading && (
        <div className="rounded-lg border border-border-default bg-bg-primary p-10 text-center text-text-secondary text-sm">
          Loading…
        </div>
      )}

      {/* Empty-state — first-time users with nothing scored / created yet.
          Only shown when (a) the request has actually completed (data is
          not null), (b) results array is empty, AND (c) no active search
          (so "no results for X" doesn't collide with "you haven't created
          anything"). Guarded against null `data` — initial state before
          the API responds. */}
      {!loading && data && (data.results?.length ?? 0) === 0 && !searchInput && !searchUsedRef.current && (
        <div className="rounded-2xl border border-border-default bg-bg-primary p-8 text-center">
          <CirclePlus className="w-8 h-8 text-text-secondary mx-auto mb-3 opacity-60" />
          <h3 className="text-base font-semibold text-text-primary mb-1">
            No {title.toLowerCase()} yet
          </h3>
          <p className="text-sm text-text-secondary mb-5 max-w-md mx-auto">
            {title === "Players"
              ? "Add players to score them in matches. Players appear in your scorecards, stats, and leaderboards."
              : title === "Teams"
              ? "Create a team, then add players to it. Teams are what you pick from when setting up a match."
              : title === "Stadiums"
              ? "Stadiums (grounds) are referenced when scheduling matches and showing scorecards."
              : `Create your first ${title.replace(/s$/, "").toLowerCase()} to get started.`}
          </p>
          <a
            href={getAddPath()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-button-primary text-white text-sm font-semibold hover:brightness-110"
          >
            <CirclePlus className="w-4" />
            Add your first {title.replace(/s$/, "").toLowerCase()}
          </a>
        </div>
      )}

      {/* Table — render when there's at least one row OR an active search
          (so users see "0 results for 'foo'" rather than the empty-state
          card). `data` may still be null during the first render before
          fetchFn resolves; the inner condition tolerates that. */}
      {!loading && ((data?.results?.length ?? 0) > 0 || searchInput) && (
        <div>
          <Table
            columns={columns}
            data={data}
            sortState={sortState}
            onSortChange={setSortState}
            selectable={selectable}
            selectedIds={selectedIds}
            onToggleId={toggleId}
            onToggleAll={toggleAll}
            rowActions={(row) => (
              // Phones: overflow-x-auto so 5–10 buttons scroll horizontally
              // instead of wrapping to multiple lines (2026-05-27 audit).
              // sm+: flex-wrap stays well-behaved.
              <div className="flex items-center gap-2 overflow-x-auto sm:overflow-visible max-w-[60vw] sm:max-w-none">
                {extraRowActions && extraRowActions(row)}

                {!row.is_locked ? (
                  <>
                    <a href={getEditPath(row.id)}>
                      <button disabled={row.is_locked} title="Edit">
                        <SquarePen className="w-4 text-text-secondary hover:text-button-primary transition-colors" />
                      </button>
                    </a>
                    {isSuperuser && (
                      <button
                        disabled={row.is_locked}
                        onClick={() => setDeleteId(row.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 text-text-secondary hover:text-button-primary transition-colors" />
                      </button>
                    )}
                  </>
                ) : (
                  <ShieldBan className="w-4 text-text-secondary" title="Locked" />
                )}
              </div>
            )}
          />

          <Pagination
            count={total}
            page={page}
            onPage={setPage}
          />
        </div>
      )}

      <Modal
        open={!!deleteId}
        title="Delete this entry?"
        body="This action cannot be undone."
        confirmLabel="Delete"
        confirmDestructive
        onConfirm={async () => {
          await deleteFn(deleteId);
          setDeleteId(null);
          load();
        }}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
